import styled from "@emotion/styled";
import { keyframes } from "@emotion/css";
import { hideScrollbar } from "src/scrollbar";
import { Container } from "src/atoms/Container";
import { Heading } from "@otrium/atoms";

const animation = keyframes`
    0% {
      max-height: 0;
    }
    100% {
      max-height: 100dvh;
    }
`;

export const ResultsContent = styled.div<{ out?: boolean; duration?: number }>`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.space.space64};
  padding-block-start: ${({ theme }) => theme.space.space32};
  padding-block-end: ${({ theme }) => theme.space.space48};
  height: 100%;

  transition: max-height 350ms ease-in-out;

  animation: ${animation} 350ms cubic-bezier(0.25, 0.1, 0.25, 1);

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    row-gap: ${({ theme }) => theme.space.space32};
    padding-block-start: ${({ theme }) => theme.space.space16};
    padding-block-end: ${({ theme }) => theme.space.space32};
  }
`;

export const StyledSearchMenu = styled.div`
  display: grid;
  width: 100%;
  grid-template-rows: 0fr;
  grid-template-columns: 100%;
  transition: grid-template-rows 350ms ease-out;
  overflow: hidden;

  &.open {
    grid-template-rows: 1fr;
    transition: grid-template-rows 350ms ease-in;
  }

  .search-menu-content-wrapper {
    width: 100%;
    background-color: ${({ theme }) => theme.colors.tone.white};
    overflow: auto;
    max-height: 100%;

    ${hideScrollbar}
  }
`;

export const SearchResultContentContainer = styled(Container)`
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  ${hideScrollbar}

  max-height: calc(
    100dvh -
      (
        (${({ theme }) => theme.space.space64} * 2) +
          ${({ theme }) => theme.space.space48}
      )
  );
`;

export const SuggestionListWrapper = styled.ul`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.space.space16};
`;

export const SuggestionItemWrapper = styled.li`
  display: flex;
  align-items: center;
  cursor: pointer;

  a {
    color: ${({ theme }) => theme.colors.tone.black};
  }

  .suggestion-item-type {
    color: ${({ theme }) => theme.colors.tone.anthracite};
  }

  .suggestion-item-breadcrumb {
    flex-basis: 100%;
    color: ${({ theme }) => theme.colors.tone.anthracite};
  }
`;

export const DefaultResultsWrapper = styled(Container)`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.space.space64};
  padding-block: ${({ theme }) => theme.space.space32};
  overflow-y: auto;
  overflow-x: hidden;
  ${hideScrollbar}

  max-height: calc(
    100dvh -
      (
        (${({ theme }) => theme.space.space64} * 2) +
          ${({ theme }) => theme.space.space48}
      )
  );

  .default-result-wrapper {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto 1fr;
    row-gap: ${({ theme }) => theme.space.space24};
  }

  .default-result-items-wrapper {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: 1fr;
    gap: ${({ theme }) => theme.space.space24};
  }

  .default-result-item {
    position: relative;
    height: 100%;

    a {
      display: grid;
      grid-template-column: 1fr;
      grid-auto-rows: auto;
      row-gap: ${({ theme }) => theme.space.space16};
      place-items: center;
      width: 100%;
      height: 100%;
    }

    img {
      width: 100%;
      height: auto;
      display: block;
      object-fit: cover;
    }

    span {
      display: block;
      align-self: self-end;
    }
  }
`;

export const ProductSuggestionsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${({ theme }) => theme.space.space24};

  &.no-results-suggestions {
    padding-block-start: ${({ theme }) => theme.space.space64};

    @media screen and (max-width: ${({ theme }) => theme.breakpoints.sm}) {
      padding-block-start: ${({ theme }) => theme.space.space32};
    }
  }
`;

export const ResultHeaderWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

export const ShopTypeButtonsWrapper = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(var(--number-of-items, 3), 1fr);
  grid-template-rows: ${({ theme }) => theme.space.space32};
  margin-block: ${({ theme }) => theme.space.space16};
  background-color: ${({ theme }) => theme.colors.tone.marble};

  &::after {
    content: "";
    position: absolute;
    left: -${({ theme }) => theme.space.space16};
    bottom: -${({ theme }) => theme.space.space16};
    height: 1px;
    width: 100vw;
    background-color: ${({ theme }) => theme.colors.tone.silver};
  }

  .shop-type-button {
    position: relative;
    font-size: ${({ theme }) => theme.fontSizes.fontSize16};
    background: transparent;
    border: none;
    color: ${({ theme }) => theme.colors.tone.anthraciteLight};
    cursor: pointer;
    transition: color 350ms ease-out;

    &.selected {
      font-weight: 600;
      color: ${({ theme }) => theme.colors.tone.black};
      transition: color 350ms ease-in;
    }

    &:not(:first-of-type)::after {
      content: "";
      position: absolute;
      left: 0;
      top: 0;
      bottom: 0;
      height: calc(100% - 16px);
      width: 1px;
      margin: auto;
      background-color: ${({ theme }) => theme.colors.tone.silver};
    }
  }
`;

export const ResultHeaderTitle = styled(Heading)`
  line-height: 32px;
  color: ${({ theme }) => theme.colors.tone.black};
  font-size: ${({ theme }) => theme.fontSizes.fontSize20};
  font-family: PlusJakartaSans;
  overflow-wrap: break-word;

  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    line-height: 40px;
    font-size: ${({ theme }) => theme.fontSizes.fontSize24};
  }
`;
